import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../designSystem/Button';
import { errorHandler } from '../../functions/errors';
import { showAlert } from '../../functions/utils';

interface DeleteItemResponse {
  displayMessage?: string;
}

type ActionType = 'delete' | 'reverse';
type WarningDialogMode = 'action' | 'info';

interface WarningDeleteDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  warningMessages?: string[];
  deleteItem: (dispatch: Dispatch) => Promise<DeleteItemResponse | void>;
  onDeleteSuccess?: () => void;
  confirmationText?: string;
  requireConfirmation?: boolean;
  children?: React.ReactNode;
  successMessage?: string;
  icon?: React.ReactNode;
  actionType?: ActionType;
  mode?: WarningDialogMode;
  severity?: 'error' | 'info' | 'success' | 'warning';
}

const WarningDeleteDialog: React.FC<WarningDeleteDialogProps> = ({
  open,
  setOpen,
  title,
  warningMessages = [],
  deleteItem,
  onDeleteSuccess,
  confirmationText = 'Confirmo que entiendo las consecuencias de esta acción',
  requireConfirmation = true,
  children,
  successMessage,
  icon,
  actionType = 'delete',
  mode = 'action',
  severity,
}) => {
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const dispatch = useDispatch();

  const isInfoMode = mode === 'info';

  useEffect(() => {
    if (open) {
      setAgreeTerms(false);
    }
  }, [open]);

  const getActionConfig = () => {
    switch (actionType) {
      case 'reverse':
        return {
          buttonText: 'Reversar',
          buttonIcon: <UndoIcon />,
          defaultSuccessMessage: 'La reversión se ha completado exitosamente.',
          dependenciesText: 'Se revertirán todas las dependencias',
        };
      case 'delete':
      default:
        return {
          buttonText: 'Eliminar',
          buttonIcon: <DeleteIcon />,
          defaultSuccessMessage: 'El elemento ha sido eliminado exitosamente.',
          dependenciesText: 'Se eliminarán todas las dependencias',
        };
    }
  };

  const actionConfig = getActionConfig();

  const alertSeverity: 'error' | 'info' | 'success' | 'warning' =
    severity || (isInfoMode ? 'info' : 'warning');

  const getSeverityConfig = () => {
    const configs = {
      success: {
        bgcolor: 'success.lighter',
        borderColor: 'success.light',
        iconColor: 'success.main',
        icon: <CheckCircleIcon />,
      },
      info: {
        bgcolor: 'info.lighter',
        borderColor: 'info.light',
        iconColor: 'info.main',
        icon: <InfoIcon />,
      },
      warning: {
        bgcolor: 'warning.lighter',
        borderColor: 'warning.light',
        iconColor: 'warning.main',
        icon: <WarningAmberIcon />,
      },
      error: {
        bgcolor: 'error.lighter',
        borderColor: 'error.light',
        iconColor: 'error.main',
        icon: <ErrorIcon />,
      },
    };
    return configs[alertSeverity];
  };

  const severityConfig = getSeverityConfig();

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteItem(dispatch);
      const message =
        response?.displayMessage ||
        successMessage ||
        actionConfig.defaultSuccessMessage;
      showAlert(dispatch, 'success', message);
      setOpen(false);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (value: boolean) => {
    if (!loading) {
      setOpen(value);
      if (!value) {
        setAgreeTerms(false);
      }
    }
  };

  const handleCancel = () => {
    if (!loading) {
      setOpen(false);
      setAgreeTerms(false);
    }
  };

  const canDelete = !requireConfirmation || agreeTerms;

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleDialogClose}>
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            p: 2,
            mx: 3,
            mt: 3,
            bgcolor: severityConfig.bgcolor,
            border: '1px solid',
            borderColor: severityConfig.borderColor,
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: 1,
            }}
          >
            {icon || (
              <Box
                sx={{
                  color: severityConfig.iconColor,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {severityConfig.icon}
              </Box>
            )}
            <Typography fontWeight={600} variant="body1">
              {title}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => handleDialogClose(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2}>
          {warningMessages.length > 0 && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'warning.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="warning.dark"
                sx={{ mb: 0.5 }}
              >
                {isInfoMode ? 'Detalle' : actionConfig.dependenciesText}
              </Typography>
              {!isInfoMode && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Esta acción no se puede deshacer
                </Typography>
              )}
              <Stack spacing={0.5}>
                {warningMessages.map((message, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                    component="div"
                  >
                    - {message}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {children}

          {requireConfirmation && !isInfoMode && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setAgreeTerms(!agreeTerms)}
                    checked={agreeTerms}
                    sx={{ py: 0 }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {confirmationText}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      {!isInfoMode && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            onClick={handleCancel}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={handleDelete}
            color="error"
            disabled={!canDelete || loading}
            loading={loading}
            startIcon={actionConfig.buttonIcon}
          >
            {actionConfig.buttonText}
          </CustomButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default WarningDeleteDialog;
